import { useCallback, useMemo, useState } from "react";

import { ViewMode } from "psims/react/pages/primary-pages/data-submissions/shared/use-view-mode";
import StepLabel from "psims/react/pages/primary-pages/data-submissions/shared/step-label";
import { DataSubmissionStatus } from "psims/models/data-submission";

export type PageSavedMap<TFormDataKey extends string> = {[key in TFormDataKey]: boolean;}

type ProgressStep = {
    hasData: boolean | null;
    kind: 'submit' | 'data';
    label: string;
}

interface UseProgressProps<TFormDataKey extends string> {
    orderedFormDataPageSavedKeys: Array<TFormDataKey>;
    steps: Array<ProgressStep>;
    submissionFormData: PageSavedMap<TFormDataKey>;
    submissionStatus: DataSubmissionStatus;
    viewMode: ViewMode;
}

function useProgress<TFormDataKey extends string>({
    orderedFormDataPageSavedKeys, steps, submissionFormData, submissionStatus, viewMode
}: UseProgressProps<TFormDataKey>) {
    const [currentStepIndex, setCurrentStepIndex] = useState(
        () => determineFirstPageIndex(submissionFormData, submissionStatus, orderedFormDataPageSavedKeys, viewMode)
    );

    // Stable serialised keys string — busts only when the actual keys change,
    // not when the caller passes a new array reference on every render.
    const keysSignature = useMemo(
        () => orderedFormDataPageSavedKeys.join(','),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orderedFormDataPageSavedKeys.join(',')]
    );

    // Stable serialised saved-state string — busts only when a flag flips.
    const formDataSignature = useMemo(
        () => orderedFormDataPageSavedKeys.map(k => `${k}:${submissionFormData[k]}`).join(','),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orderedFormDataPageSavedKeys.join(','), submissionFormData]
    );

    const progressSteps = useMemo(() => {
        return steps.map((step, index) => {
            const savedKey = orderedFormDataPageSavedKeys[index];
            return {
                ...step,
                index,
                // StepLabel is called as a plain function — wrap result so the
                // reference only changes when hasData or label actually change.
                Label: StepLabel({
                    hasData: step.kind === 'submit' ? true : Boolean(step.hasData),
                    label: step.label,
                }),
                status: (
                    currentStepIndex === index
                        ? 'active'
                        : statusForStep(step, submissionFormData, savedKey, viewMode)
                ) as 'active' | 'complete' | 'pending',
            };
        });
    // Use signatures instead of the raw objects/arrays so this memo only busts
    // when values actually change, not when references change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStepIndex, keysSignature, formDataSignature, steps, viewMode]);

    const currentStep = useMemo(
        () => progressSteps[currentStepIndex],
        [currentStepIndex, progressSteps]
    );

    const goToStep = useCallback((index: number) => {
        setCurrentStepIndex(index);
    }, []);

    return useMemo(() => ({
        currentStep,
        progressSteps,
        goToStep,
    }), [currentStep, progressSteps, goToStep]);
}

export default useProgress;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function determineFirstPageIndex<TFormDataKey extends string>(
    submissionFormData: PageSavedMap<TFormDataKey>,
    submissionStatus: DataSubmissionStatus,
    orderedFormDataPageSavedKeys: Array<TFormDataKey>,
    viewMode: ViewMode
) {
    if (viewMode !== 'edit' || submissionStatus === 'Action required') {
        return 0;
    }

    let i = 0;
    for (; i < orderedFormDataPageSavedKeys.length; i++) {
        if (!submissionFormData[orderedFormDataPageSavedKeys[i]]) {
            return 0;
        }
    }

    return i;
}

function statusForStep<TFormDataKey extends string>(
    step: ProgressStep,
    submissionFormData: PageSavedMap<TFormDataKey>,
    savedPageKey: TFormDataKey,
    viewMode: ViewMode
) {
    if (viewMode === 'view') {
        return 'complete';
    }

    if (step.kind === 'submit') {
        return 'pending';
    }

    return submissionFormData[savedPageKey] ? 'complete' : 'pending';
}